// Shared body of the matter quick-view popup, used by:
//  - Matters.js: rendered INSIDE a DraggablePopup as an in-window overlay
//  - pages/PopupMatter.js: rendered standalone at /popup/matter/:id when the
//    user pops it out to its own OS window (Electron-only)
// Both surfaces share this component so a fix lands in one place.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { roundBillable } from '../utils/format'

const statusColor = (s) => s === 'active' ? 'bg-green-100 text-green-700' :
  s === 'closed' ? 'bg-gray-200 text-gray-700' : 'bg-yellow-100 text-yellow-700'

export default function MatterQuickViewContent({ matterId, summary, onClose, standalone = false }) {
  const navigate = useNavigate()
  // When the component is rendered IN-WINDOW (Matters.js), the caller may
  // already have a summary row from the matters list and pass it via the
  // `summary` prop to render instantly while the detail fetch is in
  // flight.  When STANDALONE (/popup/matter/:id), there's no summary — we
  // fetch the matter detail and use its top-level fields as the header.
  const [matter, setMatter] = useState(summary || null)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(!summary)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!matterId) return
    let cancelled = false
    api.get(`/api/matters/${matterId}`).then(res => {
      if (cancelled) return
      setDetail(res.data)
      // If we didn't have a summary passed in, hydrate from the detail
      if (!summary) setMatter(res.data)
      setLoading(false)
    }).catch(err => {
      if (cancelled) return
      setError(err.response?.data?.error || 'Could not load matter')
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [matterId, summary])

  const go = (path) => {
    // In standalone (popped-out) mode, navigating inside the popup window
    // doesn't make sense — instead, close the popup and bring focus back
    // to the main window where the route lives.  Electron handles this
    // via the closePopupWindow IPC.  In-window mode just navigates.
    if (standalone && window.electronAPI?.closePopupWindow) {
      window.electronAPI.closePopupWindow(`matter-${matterId}`)
      return
    }
    if (onClose) onClose()
    navigate(path)
  }

  if (loading) return <div className="p-5 text-center text-gray-400 text-sm">Loading…</div>
  if (error) return <div className="p-5 text-center text-red-600 text-sm">{error}</div>
  if (!matter) return null

  return (
    <div className="p-5">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800">{matter.title}</h2>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {matter.file_number && <span className="text-xs text-gray-500 font-mono">#{matter.file_number}</span>}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(matter.status)}`}>{matter.status}</span>
          {matter.practice_area && <span className="text-xs text-gray-500">{matter.practice_area}</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <div><span className="text-gray-500">Client:</span> <span className="font-medium">{matter.first_name} {matter.last_name}</span></div>
        <div><span className="text-gray-500">Attorney:</span> <span className="font-medium">{matter.responsible_attorney_name || '—'}</span></div>
        <div className="col-span-2"><span className="text-gray-500">Billing:</span> <span className="font-medium capitalize">{matter.billing_type?.replace('_', ' ')}{matter.rate ? ` @ $${matter.rate}/hr` : ''}</span></div>
      </div>

      {detail?.summary && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-yellow-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Unposted</p>
            <p className="text-sm font-bold text-yellow-600">{(detail.summary.unposted_hours || 0).toFixed(1)} hrs</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">WIP</p>
            <p className="text-sm font-bold text-blue-600">${parseFloat(detail.summary.wip_amount || 0).toFixed(2)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Total Costs</p>
            <p className="text-sm font-bold text-orange-600">${parseFloat(detail.summary.total_costs || 0).toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Total Billed</p>
            <p className="text-sm font-bold text-gray-800">${parseFloat(detail.summary.total_billed || 0).toFixed(2)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Total Paid</p>
            <p className="text-sm font-bold text-green-600">${parseFloat(detail.summary.total_paid || 0).toFixed(2)}</p>
          </div>
          <div className={`rounded-lg p-2 text-center ${parseFloat(detail.summary.ar_balance || 0) > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <p className="text-xs text-gray-500">AR Balance</p>
            <p className={`text-sm font-bold ${parseFloat(detail.summary.ar_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ${parseFloat(detail.summary.ar_balance || 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {detail?.time_entries?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Recent Time ({detail.time_entries.length})</p>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {detail.time_entries.slice(0, 5).map(te => (
              <div key={te.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                <span className="text-gray-600 truncate flex-1">{te.description || '—'}</span>
                <span className="text-gray-500 ml-2 whitespace-nowrap">{roundBillable(te.hours).toFixed(1)}h</span>
                <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${te.status === 'posted' ? 'bg-blue-100 text-blue-600' : te.status === 'billed' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>{te.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <button onClick={() => go(`/matters/${matterId}`)}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
          Open Full Detail
        </button>
        <button onClick={() => go(`/matters/${matterId}?tab=billing`)}
          className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
          Billing
        </button>
        {!standalone && onClose && (
          <button onClick={onClose}
            className="ml-auto px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Close
          </button>
        )}
      </div>
    </div>
  )
}
